'use client';

import React from 'react';
import Breadcrumbs from '@/components/Breadcrumbs';
import PromptCard from '@/components/PromptCard';
import CategoryCard from '@/components/CategoryCard';
import FAQAccordion from '@/components/FAQAccordion';
import { Hash, BookOpen, ChevronRight, Lightbulb, CheckCircle2 } from 'lucide-react';
import { getRelatedCategories } from '@/data/categories';
import type { Category } from '@/data/categories';
import type { Prompt } from '@/data/prompts';

interface CategoryPageContentProps {
  category: Category;
  prompts: Prompt[];
}

export default function CategoryPageContent({ category, prompts }: CategoryPageContentProps) {
  const relatedCategories = getRelatedCategories(category.slug);

  const filteredPrompts = prompts;

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.name,
    description: category.description,
    url: `https://pinuprompt.site/categories/${category.slug}`,
    numberOfItems: category.promptCount,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Breadcrumbs */}
        <div className="py-4">
          <Breadcrumbs
            items={[
              { label: 'Categories', href: '/categories' },
              { label: category.name },
            ]}
          />
        </div>

        {/* Hero Banner */}
        <div className="relative rounded-3xl overflow-hidden mb-10 min-h-[220px] border border-border bg-gradient-to-br from-accent via-white to-pink-50">
          <div className="relative flex min-h-[220px] flex-col justify-end p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="badge-pink text-xs">{category.group}</span>
              <span className="flex items-center gap-1 text-muted-foreground text-xs">
                <Hash size={11} />
                {category.promptCount} prompts
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-3">
              {category.name} Prompts
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base max-w-2xl leading-relaxed">
              {category.description}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Introduction */}
            <div className="bg-white rounded-2xl border border-border p-6 mb-8 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={16} className="text-primary" />
                <h2 className="font-bold text-foreground">About {category.name} Prompts</h2>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{category.longDescription}</p>
            </div>

            {/* Prompt Collection */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-foreground mb-4">
                {category.name} Prompt Collection
              </h2>

              {filteredPrompts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
                  {filteredPrompts.map((prompt) => (
                    <PromptCard key={`cat-prompt-${prompt.id}`} prompt={prompt} variant="default" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-muted/30 rounded-2xl border border-border">
                  <p className="text-muted-foreground font-medium">No prompts found.</p>
                </div>
              )}
            </div>

            {/* Tips Section */}
            <div className="bg-gradient-to-br from-accent to-pink-100 rounded-2xl border border-pink-200 p-6 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb size={16} className="text-primary" />
                <h2 className="font-bold text-foreground">Tips for Better {category.name} Prompts</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {category.tips.map((tip, i) => (
                  <div key={`cat-tip-${i}`} className="flex items-start gap-2.5">
                    <CheckCircle2 size={16} className="text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-foreground leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-8">
              <FAQAccordion
                faqs={category.faqs}
                title={`${category.name} Prompt FAQ`}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Category Stats */}
            <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
              <h3 className="font-bold text-foreground text-sm mb-4">Category Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Prompts</span>
                  <span className="font-semibold text-foreground">{category.promptCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Group</span>
                  <span className="font-semibold text-foreground text-right">{category.group}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Prompt Types</span>
                  <span className="font-semibold text-foreground">{category.promptTypes.length}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground font-medium mb-2 uppercase tracking-wider">Prompt Types</p>
                <div className="flex flex-wrap gap-1.5">
                  {category.promptTypes.map((type) => (
                    <span key={`sidebar-type-${type}`} className="badge-muted text-xs">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Related Categories */}
            {relatedCategories.length > 0 && (
              <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
                <h3 className="font-bold text-foreground text-sm mb-4">Related Categories</h3>
                <div className="space-y-2">
                  {relatedCategories.map((cat) => (
                    <CategoryCard key={`sidebar-related-${cat.slug}`} category={cat} variant="compact" />
                  ))}
                </div>
              </div>
            )}

            {/* All Categories Link */}
            <a
              href="/categories"
              className="flex items-center justify-between p-4 rounded-xl bg-accent border border-pink-200 hover:bg-pink-100 transition-colors group"
            >
              <span className="text-sm font-semibold text-primary">Browse All Categories</span>
              <ChevronRight size={16} className="text-primary group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
